import { useId, useMemo, useState, useEffect, useRef } from "react";
import { torontoDateParts } from "@utils";
import Button from "./Button";
import FieldWrapper from "./FieldWrapper";

const MONTHS = [
  { key: "01", label: "Jan" },
  { key: "02", label: "Feb" },
  { key: "03", label: "Mar" },
  { key: "04", label: "Apr" },
  { key: "05", label: "May" },
  { key: "06", label: "Jun" },
  { key: "07", label: "Jul" },
  { key: "08", label: "Aug" },
  { key: "09", label: "Sep" },
  { key: "10", label: "Oct" },
  { key: "11", label: "Nov" },
  { key: "12", label: "Dec" },
];

function pad2(n) {
  return String(n).padStart(2, "0");
}

/**
 * Custom month picker (YYYY-MM) with year navigation and optional “disable current & future”.
 * Use when you need a custom UX (dropdown with year + month grid) or disableCurrentAndFuture behavior.
 * For a simple native browser month input, use MonthField instead.
 *
 * value: "YYYY-MM"
 * onChange: (newValue: "YYYY-MM") => void
 *
 * @see MonthField – native input type="month", simpler and more compact
 */
export default function MonthPicker({
  value,
  onChange,
  label = "Month",
  required,
  error,
  showError,
  disableCurrentAndFuture = true,
  className = "",
  id: idProp,
}) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const now = useMemo(() => torontoDateParts(), []);
  const currentYear = now.year;
  const currentMonthIndex = now.month; // 1..12
  // From the 25th of the current month onwards we allow selecting the
  // current month so users can start finalizing this reporting period a
  // few days early (e.g. on Mar 25 we open March for selection).
  const allowCurrentMonth = now.day >= 25;

  const selectedYear = value ? Number(value.slice(0, 4)) : currentYear;
  const selectedMonth = value ? value.slice(5, 7) : pad2(Math.max(1, currentMonthIndex - 1));

  const [panelYear, setPanelYear] = useState(selectedYear);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => setPanelYear(selectedYear), [selectedYear]);

  // close on outside click
  useEffect(() => {
    function onDocClick(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }

    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const prettyValue = useMemo(() => {
    const m = MONTHS.find((x) => x.key === selectedMonth)?.label || selectedMonth;
    return `${m} ${selectedYear}`;
  }, [selectedMonth, selectedYear]);

  const isDisabledMonth = (year, monthKey) => {
    if (!disableCurrentAndFuture) return false;

    const m = Number(monthKey);

    // disable future years
    if (year > currentYear) return true;

    // disable months strictly in the future
    if (year === currentYear && m > currentMonthIndex) return true;

    // current month itself: only enabled from the 25th onward
    if (year === currentYear && m === currentMonthIndex) return !allowCurrentMonth;

    return false;
  };

  const handleSelect = (year, monthKey) => {
    if (isDisabledMonth(year, monthKey)) return;
    onChange?.(`${year}-${monthKey}`);
    setOpen(false);
  };

  const canGoNextYear = !disableCurrentAndFuture || panelYear < currentYear; // when disabling future, don't allow selecting year above current

  return (
    <FieldWrapper
      id={id}
      label={label}
      required={required}
      error={error}
      showError={showError}
      className={className}
    >
      <div className="relative w-full" ref={wrapRef}>
        {/* Trigger */}
        <Button
          id={id}
          type="button"
          variant="outline"
          size="md"
          onClick={() => setOpen((v) => !v)}
          aria-describedby={showError && error ? `${id}-error` : undefined}
          className="mt-0 w-full max-w-[240px] justify-between"
        >
          <span className="font-semibold">{prettyValue}</span>
          <span className="text-palette-gray-secondary">▾</span>
        </Button>

        {/* Panel */}
        {open && (
          <div className="absolute z-50 mt-2 w-[280px] rounded-2xl border border-palette-gray-divider bg-white p-4 shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setPanelYear((y) => y - 1)}
                className="h-9 w-9 !px-0 !py-0 border border-palette-gray-divider rounded-lg hover:bg-palette-gray-soft"
                aria-label="Previous year"
              >
                ◀
              </Button>

              <div className="text-sm font-bold">{panelYear}</div>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!canGoNextYear}
                onClick={() => setPanelYear((y) => y + 1)}
                className="h-9 w-9 !px-0 !py-0 border border-palette-gray-divider rounded-lg hover:bg-palette-gray-soft"
                aria-label="Next year"
              >
                ▶
              </Button>
            </div>

            {/* Months */}
            <div className="grid grid-cols-3 gap-2">
              {MONTHS.map((m) => {
                const disabled = isDisabledMonth(panelYear, m.key);
                const selected = value === `${panelYear}-${m.key}`;

                return (
                  <Button
                    key={m.key}
                    type="button"
                    variant={selected ? "primary" : "outline"}
                    size="sm"
                    disabled={disabled}
                    onClick={() => handleSelect(panelYear, m.key)}
                    className={[
                      "w-full",
                      selected ? "!bg-black !hover:bg-black !focus:ring-palette-gray-text" : "",
                      disabled ? "opacity-40 cursor-not-allowed" : "",
                    ].join(" ")}
                  >
                    {m.label}
                  </Button>
                );
              })}
            </div>

            {disableCurrentAndFuture && (
              <div className="mt-3 text-xs text-palette-gray-secondary">
                Current &amp; future months are disabled.
              </div>
            )}
          </div>
        )}
      </div>
    </FieldWrapper>
  );
}
